import { requestFCMToken, isFirebaseConfigured } from './firebase';
import { restFeathers } from './feathersClient';

export interface BrowserNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  data?: {
    link?: string;
    notificationId?: number;
    conversation_id?: string;
    [key: string]: any;
  };
}

export const requestNotificationPermission = async (userId?: number): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support desktop notifications');
    return 'denied';
  }

  let permission = Notification.permission;

  if (permission !== 'granted' && permission !== 'denied') {
    permission = await Notification.requestPermission();
  }

  if (permission === 'granted' && userId && isFirebaseConfigured()) {
    try {
      await registerFCMToken(userId);
    } catch (error) {
      console.error('Error registering FCM token:', error);
    }
  }

  return permission;
};

export const registerFCMToken = async (userId: number): Promise<boolean> => {
  try {
    if (!isFirebaseConfigured()) {
      console.warn('Firebase not configured, skipping FCM token registration');
      return false;
    }

    const token = await requestFCMToken();
    
    if (!token) {
      console.warn('Failed to get FCM token');
      return false;
    }

    const existingDevices = await restFeathers.service('user-devices').find({
      query: {
        user_id: userId,
        device_token: token,
        device_type: 'web',
        $limit: 1
      }
    });

    if (existingDevices.data && existingDevices.data.length > 0) {
      await restFeathers.service('user-devices').patch(existingDevices.data[0].id, {
        is_active: true,
        last_login_at: new Date().toISOString()
      });
      console.log('FCM token reactivated');
    } else {
      await restFeathers.service('user-devices').create({
        user_id: userId,
        device_token: token,
        device_type: 'web',
        device_name: getBrowserName(),
        device_model: getDeviceModel(),
        is_active: true,
        last_login_at: new Date().toISOString()
      });
      console.log('FCM token registered successfully');
    }

    return true;
  } catch (error) {
    console.error('Error registering FCM token:', error);
    return false;
  }
};

const getBrowserName = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Unknown Browser';
};

const getDeviceModel = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Linux')) return 'Linux';
  return 'Unknown OS';
};

export const isTabVisible = (): boolean => {
  return !document.hidden;
};

export const showBrowserNotification = (options: BrowserNotificationOptions): Notification | null => {
  if (!('Notification' in window)) {
    console.warn('[BrowserNotification] This browser does not support desktop notifications');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('[BrowserNotification] Notification permission not granted');
    return null;
  }

  const conversationId = options.data?.conversation_id;
  if (conversationId) {
    const url = window.location.href;
    if (url.includes(`conversationId=${conversationId}`) || url.includes(`/conversations/${conversationId}`)) {
      return null;
    }
  }

  const notification = new Notification(options.title, {
    body: options.body,
    icon: options.icon || '/images/achieveapex-icon.png',
    badge: '/images/achieveapex-icon.png',
    tag: options.data?.conversation_id || options.data?.notificationId?.toString() || 'notification',
    requireInteraction: false,
    data: options.data,
  });

  notification.onclick = (event) => {
    event.preventDefault();
    window.focus();

    if (options.data?.link) {
      window.location.href = options.data.link;
    }

    notification.close();
  };

  return notification;
};

export const hasNotificationSupport = (): boolean => {
  return 'Notification' in window;
};

export const getNotificationPermissionStatus = (): NotificationPermission => {
  if (!hasNotificationSupport()) {
    return 'denied';
  }
  return Notification.permission;
};
